import type {
  ContentFitnessCoverageStatus,
  ContentFitnessVerificationState,
} from "../../enums/content-fitness";
import {
  CONTENT_FITNESS_COVERAGE_STATUS,
  CONTENT_FITNESS_VERIFICATION_STATE,
} from "../../enums/content-fitness";
import {
  CONTENT_FITNESS_DIMENSIONS,
  type ContentFitnessDimensionKey,
} from "./dimensions";
import {
  CONTENT_FITNESS_PILOT_SUBCRITERIA,
  CONTENT_FITNESS_SUBCRITERIA,
} from "./subcriteria";
import { CONTENT_FITNESS_FRAMEWORK_VERSION } from "./version";

export type ContentFitnessRollupInput = {
  subcriterionKey: string;
  verificationState: ContentFitnessVerificationState;
  score: number | null;
};

export type ContentFitnessDimensionRollup = {
  key: ContentFitnessDimensionKey;
  label: string;
  weight: number;
  score: number | null;
  assessedSubcriteria: number;
  expectedSubcriteria: number;
  canonicalSubcriteria: number;
  coverageStatus: ContentFitnessCoverageStatus;
  notApplicable: number;
  unableToVerify: number;
};

export type ContentFitnessRollup = {
  frameworkVersion: typeof CONTENT_FITNESS_FRAMEWORK_VERSION;
  dimensions: ContentFitnessDimensionRollup[];
  overallScore: number | null;
  overallCoverageStatus: ContentFitnessCoverageStatus;
  assessedSubcriteria: number;
  canonicalSubcriteria: number;
};

export type ContentFitnessDimensionScoreInput = {
  key: string;
  score: number;
};

export type WebsiteContentFitnessRollup = {
  dimensions: Array<{
    key: ContentFitnessDimensionKey;
    label: string;
    weight: number;
    score: number | null;
    measuredPages: number;
  }>;
  overallScore: number | null;
  coverageStatus: ContentFitnessCoverageStatus;
  scoredDimensions: number;
  canonicalDimensions: number;
  coveredWeight: number;
};

export function score0To4To100(score: number): number {
  return Math.round((score / 4) * 100);
}

/**
 * Website Content Fitness: average measured pages within each canonical
 * dimension, then apply the v1.2 dimension weights. Non-Content-Fitness keys
 * are ignored, and genuinely unscored dimensions leave the denominator.
 */
export function rollUpWebsiteContentFitness(
  scores: readonly ContentFitnessDimensionScoreInput[],
): WebsiteContentFitnessRollup {
  const valuesByDimension = new Map<ContentFitnessDimensionKey, number[]>();
  const dimensionKeys = new Set<string>(
    CONTENT_FITNESS_DIMENSIONS.map((dimension) => dimension.key),
  );
  for (const row of scores) {
    if (!dimensionKeys.has(row.key)) {
      continue;
    }
    const key = row.key as ContentFitnessDimensionKey;
    valuesByDimension.set(key, [
      ...(valuesByDimension.get(key) ?? []),
      row.score,
    ]);
  }

  const dimensions = CONTENT_FITNESS_DIMENSIONS.map((dimension) => {
    const values = valuesByDimension.get(dimension.key) ?? [];
    return {
      key: dimension.key,
      label: dimension.label,
      weight: dimension.weight,
      score:
        values.length === 0
          ? null
          : Math.round(
              values.reduce((sum, score) => sum + score, 0) / values.length,
            ),
      measuredPages: values.length,
    };
  });
  const scored = dimensions.filter((dimension) => dimension.score !== null);
  const coveredWeight = scored.reduce(
    (sum, dimension) => sum + dimension.weight,
    0,
  );
  const overallScore =
    coveredWeight === 0
      ? null
      : Math.round(
          scored.reduce(
            (sum, dimension) =>
              sum + (dimension.score as number) * dimension.weight,
            0,
          ) / coveredWeight,
        );

  return {
    dimensions,
    overallScore,
    coverageStatus:
      scored.length === 0
        ? CONTENT_FITNESS_COVERAGE_STATUS.UNSCORED
        : scored.length === CONTENT_FITNESS_DIMENSIONS.length
          ? CONTENT_FITNESS_COVERAGE_STATUS.FULL
          : CONTENT_FITNESS_COVERAGE_STATUS.PARTIAL,
    scoredDimensions: scored.length,
    canonicalDimensions: CONTENT_FITNESS_DIMENSIONS.length,
    coveredWeight,
  };
}

export function rollUpContentFitness(
  assessments: readonly ContentFitnessRollupInput[],
): ContentFitnessRollup {
  const bySubcriterion = new Map(
    assessments.map((assessment) => [assessment.subcriterionKey, assessment]),
  );
  const dimensions = CONTENT_FITNESS_DIMENSIONS.map((dimension) => {
    const canonical = CONTENT_FITNESS_SUBCRITERIA.filter(
      (item) => item.parentMetricKey === dimension.key,
    );
    const expected = CONTENT_FITNESS_PILOT_SUBCRITERIA.filter(
      (item) => item.parentMetricKey === dimension.key,
    );
    const rows = expected
      .map((item) => bySubcriterion.get(item.key))
      .filter((row): row is ContentFitnessRollupInput => Boolean(row));
    const scored = rows.filter(
      (row) =>
        row.verificationState === CONTENT_FITNESS_VERIFICATION_STATE.SCORED &&
        row.score !== null,
    );
    const assessedCount = scored.length;
    const score =
      scored.length === 0
        ? null
        : Math.round(
            scored.reduce(
              (sum, row) => sum + score0To4To100(row.score as number),
              0,
            ) / scored.length,
          );
    const coverageStatus: ContentFitnessCoverageStatus =
      assessedCount === 0
        ? CONTENT_FITNESS_COVERAGE_STATUS.UNSCORED
        : assessedCount === canonical.length
          ? CONTENT_FITNESS_COVERAGE_STATUS.FULL
          : CONTENT_FITNESS_COVERAGE_STATUS.PARTIAL;
    return {
      key: dimension.key,
      label: dimension.label,
      weight: dimension.weight,
      score,
      assessedSubcriteria: assessedCount,
      expectedSubcriteria: expected.length,
      canonicalSubcriteria: canonical.length,
      coverageStatus,
      notApplicable: rows.filter(
        (row) =>
          row.verificationState ===
          CONTENT_FITNESS_VERIFICATION_STATE.NOT_APPLICABLE,
      ).length,
      unableToVerify: rows.filter(
        (row) =>
          row.verificationState ===
          CONTENT_FITNESS_VERIFICATION_STATE.UNABLE_TO_VERIFY,
      ).length,
    };
  });
  const overallScore = rollUpWebsiteContentFitness(
    dimensions.flatMap((dimension) =>
      dimension.score === null
        ? []
        : [{ key: dimension.key, score: dimension.score }],
    ),
  ).overallScore;
  const assessedSubcriteria = dimensions.reduce(
    (sum, dimension) => sum + dimension.assessedSubcriteria,
    0,
  );
  const canonicalSubcriteria = CONTENT_FITNESS_SUBCRITERIA.length;
  return {
    frameworkVersion: CONTENT_FITNESS_FRAMEWORK_VERSION,
    dimensions,
    overallScore,
    overallCoverageStatus:
      assessedSubcriteria === 0
        ? CONTENT_FITNESS_COVERAGE_STATUS.UNSCORED
        : assessedSubcriteria === canonicalSubcriteria
          ? CONTENT_FITNESS_COVERAGE_STATUS.FULL
          : CONTENT_FITNESS_COVERAGE_STATUS.PARTIAL,
    assessedSubcriteria,
    canonicalSubcriteria,
  };
}

export function contentFitnessDimensionDetail(
  dimension: ContentFitnessDimensionRollup,
): string {
  const score =
    dimension.score === null ? "unscored" : `${dimension.score}/100`;
  return [
    `pilot ${score}`,
    `${dimension.assessedSubcriteria}/${dimension.canonicalSubcriteria} canonical subcriteria assessed`,
    `${dimension.coverageStatus} coverage`,
    dimension.notApplicable > 0
      ? `${dimension.notApplicable} not_applicable`
      : null,
    dimension.unableToVerify > 0
      ? `${dimension.unableToVerify} unable_to_verify`
      : null,
  ]
    .filter((part): part is string => Boolean(part))
    .join(" | ");
}
